import { useState, useMemo } from 'react';
import {
  Breadcrumbs,
  Card,
  CardContent,
  Typography,
  Grid,
  Input,
  Stack,
  FormLabel,
  Box,
  CircularProgress,
  Button,
} from '@mui/joy';
import { CreateEditUserModal } from '@/components/Pages/Users/CreateEditUserModal';
import { SetUserPasswordModal } from '@/components/Pages/Users/SetUserPasswordModal';
import { UserClaimsModal } from '@/components/Pages/Users/UserClaimsModal';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { pageTitle, usePageTitle } from '@/utils/pageTitle';
// import { DeleteUserModal } from '@/components/Pages/Users/DeleteUserModal';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/hooks/useUsers';
import { useUserClaims, useValidClaims } from '@/hooks/useClaims';
import { Security as SecurityIcon } from '@mui/icons-material';
import { PERMISSION_GROUPS } from '@/components/Pages/Users/PermissionGroupsTable';
import { PermissionGroupsTags } from '@/components/Pages/Users/PermissionGroupsTags';

export const Route = createFileRoute('/_protected/users/$id')({
  component: UserDetail,
  head: () => pageTitle('Usuarios'),
});

function UserDetail() {
  const { user: authUser, hasClaim, isLoading: authLoading, isSuperAdmin } = useAuth();
  const { id } = Route.useParams();
  const canRead = hasClaim('USERS_READ') || isSuperAdmin;
  const canWrite = hasClaim('USERS_WRITE') || isSuperAdmin;
  const canAssignRoles = hasClaim('USERS_ASSING_ROLES') || isSuperAdmin;

  const [claimsModalOpen, setClaimsModalOpen] = useState(false);
  const { data: user, isLoading } = useUser(id);
  const { data: claims } = useUserClaims(id);
  const { data: validClaims = [] } = useValidClaims({ enabled: true });
  usePageTitle(
    'Usuarios',
    [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  );

  const getClaimType = (c: { claimType?: string; ClaimType?: string }) =>
    c.ClaimType ?? c.claimType ?? '';

  const userClaimTypes = useMemo(
    () => new Set((claims ?? []).map(c => getClaimType(c as any)).filter(Boolean)),
    [claims]
  );

  const validClaimTypeSet = useMemo(
    () => new Set(validClaims.map(vc => getClaimType(vc as any))),
    [validClaims]
  );

  const availableGroups = useMemo(
    () =>
      PERMISSION_GROUPS.filter(g => g.claimTypes.every(ct => validClaimTypeSet.has(ct))),
    [validClaimTypeSet]
  );

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/users' />;
  }

  if (!user) {
    return <div>Usuario no encontrado</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/users'>Usuarios</Link>
          <Typography>
            {user?.firstName} {user?.lastName}
          </Typography>
        </Breadcrumbs>

        {canWrite && (
          <Stack direction='row' gap={1} alignItems='center'>
            <SetUserPasswordModal userId={user.id} />
            {authUser?.id === user?.id.toString() && (
              <CreateEditUserModal user={user} />
            )}
            {/* <DeleteUserModal id={user?.id.toString()} name={user?.fullName} /> */}
          </Stack>
        )}
      </Stack>

      <Card>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid xs={12} md={6}>
            <FormLabel>Nombre</FormLabel>
            <Input
              value={user?.firstName ?? ''}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Apellido</FormLabel>
            <Input
              value={user?.lastName ?? ''}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Email</FormLabel>
            <Input
              value={user?.email ?? ''}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Teléfono</FormLabel>
            <Input
              value={user?.phoneNumber ?? ''}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>DNI</FormLabel>
            <Input
              value={user?.dni ?? ''}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
        </Grid>
      </Card>

      <UserClaimsModal
        open={claimsModalOpen}
        onClose={() => setClaimsModalOpen(false)}
        userId={user.id}
      />

      <Card>
        <CardContent>
          <Stack direction='row' justifyContent='space-between' alignItems='center' marginBottom={2}>
            <Typography level='h4'>
              Permisos
            </Typography>

            {canAssignRoles && (
              <Button
                variant='outlined'
                color='neutral'
                size='sm'
                startDecorator={<SecurityIcon />}
                onClick={() => setClaimsModalOpen(true)}
              >
                Asignar permisos
              </Button>
            )}
          </Stack>
          <PermissionGroupsTags
            groups={availableGroups}
            selectedClaimTypes={userClaimTypes}
            validClaims={validClaims}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
